/**
 * Iframe / gateway tracking — dash data layer (feature #163).
 *
 * Thin typed wrapper around `apiClient` for the
 * `/api/v1/iframe-gateway` surface. Mirrors the contract in
 * `apps/api/src/routes/iframe-gateway.ts`:
 *
 *   POST   /            { name, slug, advertiser_id, allowed_origins[],
 *                         event_type? }
 *                       → { gateway: View }
 *   GET    /            → { gateways: View[] }   (?advertiser_id filters)
 *   GET    /:id         → { gateway: View }
 *   PATCH  /:id         { name?, allowed_origins?, status?, event_type? }
 *                       → { gateway: View }
 *   DELETE /:id         → { deleted: true, id } when never fired,
 *                         or { archived: true, gateway: View } when counters
 *                         have accumulated.
 *
 * Keep this module free of React imports — the page and the unit tests
 * both import from here, and the test file exercises the pure helpers
 * without pulling in a DOM.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type IframeGatewayEventType = "conversion" | "lead" | "install";
export type IframeGatewayStatus = "active" | "paused" | "archived";

export const EVENT_TYPE_LABELS: Record<IframeGatewayEventType, string> = {
	conversion: "Conversion",
	lead: "Lead",
	install: "Install",
};

export const STATUS_LABELS: Record<IframeGatewayStatus, string> = {
	active: "Active",
	paused: "Paused",
	archived: "Archived",
};

export const STATUS_ORDER: readonly IframeGatewayStatus[] = [
	"active",
	"paused",
	"archived",
];

export const EVENT_TYPES: readonly IframeGatewayEventType[] = [
	"conversion",
	"lead",
	"install",
];

export interface IframeGatewayView {
	id: string;
	tenant_id: string;
	advertiser_id: string;
	name: string;
	slug: string;
	allowed_origins: string[];
	event_type: IframeGatewayEventType;
	status: IframeGatewayStatus;
	embed_count: number;
	conversion_count: number;
	last_fired_at: number | null;
	created_at: number;
	updated_at: number;
}

// ---------------------------------------------------------------------------
// Input validation — mirrors the server-side rules so bad drafts fail in the
// browser before we POST. Do not diverge from the server regex — tests assert
// they agree.
// ---------------------------------------------------------------------------

const SLUG_RE = /^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?$/;
const RESERVED_SLUGS = new Set([
	"",
	"embed",
	"verify",
	"admin",
	"api",
	"new",
	"edit",
	"delete",
]);

export function normalizeSlug(raw: string): string {
	return (raw ?? "").trim().toLowerCase();
}

export function isValidSlug(raw: string): boolean {
	const v = normalizeSlug(raw);
	if (v.length === 0 || v.length > 64) return false;
	if (RESERVED_SLUGS.has(v)) return false;
	return SLUG_RE.test(v);
}

export function normalizeOrigin(raw: string): string | null {
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim();
	if (trimmed.length === 0 || trimmed.length > 253) return null;
	if (trimmed === "*") return "*";
	try {
		const u = new URL(trimmed);
		if (u.protocol !== "http:" && u.protocol !== "https:") return null;
		if (!u.hostname) return null;
		return `${u.protocol}//${u.host}`.toLowerCase();
	} catch {
		return null;
	}
}

export function parseOriginList(input: string): {
	ok: true;
	origins: string[];
} | { ok: false; bad: string[] } {
	const parts = input
		.split(/[\s,]+/)
		.map((s) => s.trim())
		.filter((s) => s.length > 0);
	const origins: string[] = [];
	const bad: string[] = [];
	for (const raw of parts) {
		const norm = normalizeOrigin(raw);
		if (!norm) {
			bad.push(raw);
			continue;
		}
		if (!origins.includes(norm)) origins.push(norm);
	}
	if (bad.length > 0) return { ok: false, bad };
	return { ok: true, origins };
}

// ---------------------------------------------------------------------------
// Draft + schema (hand-rolled safeParse, matches publisher-tracking-domains
// feature conventions so dash unit tests remain uniform).
// ---------------------------------------------------------------------------

export interface GatewayDraft {
	name: string;
	slug: string;
	advertiserId: string;
	allowedOriginsText: string;
	eventType: IframeGatewayEventType;
}

export const EMPTY_DRAFT: GatewayDraft = {
	name: "",
	slug: "",
	advertiserId: "",
	allowedOriginsText: "",
	eventType: "conversion",
};

export interface ValidatedGatewayDraft {
	name: string;
	slug: string;
	advertiserId: string;
	allowedOrigins: string[];
	eventType: IframeGatewayEventType;
}

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export const gatewayDraftSchema = {
	safeParse(input: GatewayDraft): ParseResult<ValidatedGatewayDraft> {
		const errors: Record<string, string> = {};

		const name = (input.name ?? "").trim();
		if (name.length === 0) {
			errors.name = "Name is required.";
		} else if (name.length > 120) {
			errors.name = "Name must be 120 characters or fewer.";
		}

		const slug = normalizeSlug(input.slug ?? "");
		if (slug.length === 0) {
			errors.slug = "Slug is required.";
		} else if (!isValidSlug(slug)) {
			errors.slug =
				"Use lowercase letters, numbers, and hyphens (e.g. shop-conf).";
		}

		const advertiserId = (input.advertiserId ?? "").trim();
		if (advertiserId.length === 0) {
			errors.advertiserId = "Advertiser ID is required.";
		} else if (advertiserId.length > 128) {
			errors.advertiserId = "Advertiser ID must be 128 chars or fewer.";
		}

		const parsed = parseOriginList(input.allowedOriginsText ?? "");
		let allowedOrigins: string[] = [];
		if (!parsed.ok) {
			errors.allowedOriginsText = `Invalid origin(s): ${parsed.bad.join(", ")}`;
		} else {
			allowedOrigins = parsed.origins;
		}

		if (!EVENT_TYPES.includes(input.eventType)) {
			errors.eventType = "Pick a valid event type.";
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				name,
				slug,
				advertiserId,
				allowedOrigins,
				eventType: input.eventType,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

export function statusBadgeClass(status: IframeGatewayStatus): string {
	switch (status) {
		case "active":
			return "bg-emerald-50 text-emerald-700 border-emerald-200";
		case "paused":
			return "bg-amber-50 text-amber-700 border-amber-200";
		case "archived":
			return "bg-zinc-100 text-zinc-600 border-zinc-200";
	}
}

export function formatTimestamp(seconds: number | null): string {
	if (seconds === null || Number.isNaN(seconds)) return "-";
	const d = new Date(seconds * 1000);
	if (Number.isNaN(d.getTime())) return "-";
	return d.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, "Z");
}

/**
 * Conversion rate = verified conversions / embed serves. Clamp to 0..1.
 */
export function conversionRate(row: IframeGatewayView): number {
	if (row.embed_count <= 0) return 0;
	const rate = row.conversion_count / row.embed_count;
	if (!Number.isFinite(rate)) return 0;
	if (rate < 0) return 0;
	if (rate > 1) return 1;
	return rate;
}

export function formatConversionRate(row: IframeGatewayView): string {
	if (row.embed_count <= 0) return "-";
	const pct = conversionRate(row) * 100;
	return `${pct.toFixed(1)}%`;
}

export function sortGateways(
	gateways: readonly IframeGatewayView[],
): IframeGatewayView[] {
	const rank: Record<IframeGatewayStatus, number> = {
		active: 0,
		paused: 1,
		archived: 2,
	};
	return gateways
		.slice()
		.sort((a, b) => {
			const r = rank[a.status] - rank[b.status];
			if (r !== 0) return r;
			return b.created_at - a.created_at;
		});
}

export function embedUrl(origin: string, gateway: IframeGatewayView): string {
	const trimmed = origin.replace(/\/+$/, "");
	return `${trimmed}/api/v1/iframe-gateway/${encodeURIComponent(gateway.slug)}/embed`;
}

export function embedSnippet(origin: string, gateway: IframeGatewayView): string {
	const url = embedUrl(origin, gateway);
	return `<iframe src="${url}" width="1" height="1" style="border:0;display:none" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>`;
}

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/iframe-gateway";

interface CreateResponse {
	gateway: IframeGatewayView;
}
interface ListResponse {
	gateways: IframeGatewayView[];
}
interface GetResponse {
	gateway: IframeGatewayView;
}
interface PatchResponse {
	gateway: IframeGatewayView;
}
export type DeleteResponse =
	| { deleted: true; id: string }
	| { archived: true; gateway: IframeGatewayView };

export async function createGateway(
	payload: ValidatedGatewayDraft,
): Promise<IframeGatewayView> {
	const res = await apiClient.post<CreateResponse>(BASE, {
		name: payload.name,
		slug: payload.slug,
		advertiser_id: payload.advertiserId,
		allowed_origins: payload.allowedOrigins,
		event_type: payload.eventType,
	});
	return res.gateway;
}

export async function listGateways(
	advertiserId?: string,
): Promise<IframeGatewayView[]> {
	const query = advertiserId?.trim()
		? { advertiser_id: advertiserId.trim() }
		: undefined;
	try {
		const res = await apiClient.get<ListResponse>(BASE, { query });
		return res.gateways ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export async function getGateway(id: string): Promise<IframeGatewayView | null> {
	if (!id.trim()) return null;
	try {
		const res = await apiClient.get<GetResponse>(
			`${BASE}/${encodeURIComponent(id)}`,
		);
		return res.gateway;
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		throw err;
	}
}

export interface PatchPayload {
	name?: string;
	allowedOrigins?: string[];
	status?: IframeGatewayStatus;
	eventType?: IframeGatewayEventType;
}

export async function patchGateway(
	id: string,
	patch: PatchPayload,
): Promise<IframeGatewayView> {
	const body: Record<string, unknown> = {};
	if (patch.name !== undefined) body.name = patch.name;
	if (patch.allowedOrigins !== undefined)
		body.allowed_origins = patch.allowedOrigins;
	if (patch.status !== undefined) body.status = patch.status;
	if (patch.eventType !== undefined) body.event_type = patch.eventType;
	const res = await apiClient.patch<PatchResponse>(
		`${BASE}/${encodeURIComponent(id)}`,
		body,
	);
	return res.gateway;
}

export async function deleteGateway(id: string): Promise<DeleteResponse> {
	const res = await apiClient.delete<DeleteResponse>(
		`${BASE}/${encodeURIComponent(id)}`,
	);
	return res;
}

// ---------------------------------------------------------------------------
// TanStack Query keys
// ---------------------------------------------------------------------------

export const iframeGatewayQueryKeys = {
	all: ["iframe-gateway"] as const,
	list: (advertiserId?: string) =>
		[...iframeGatewayQueryKeys.all, "list", advertiserId ?? "__all__"] as const,
	single: (id: string) =>
		[...iframeGatewayQueryKeys.all, "single", id] as const,
};

// ---------------------------------------------------------------------------
// Cache helpers used by the page for optimistic list mutations.
// ---------------------------------------------------------------------------

export function replaceInList(
	list: readonly IframeGatewayView[],
	next: IframeGatewayView,
): IframeGatewayView[] {
	let hit = false;
	const out = list.map((row) => {
		if (row.id === next.id) {
			hit = true;
			return next;
		}
		return row;
	});
	if (!hit) out.push(next);
	return out;
}

export function removeFromList(
	list: readonly IframeGatewayView[],
	id: string,
): IframeGatewayView[] {
	return list.filter((row) => row.id !== id);
}

export function countByStatus(
	list: readonly IframeGatewayView[],
): Record<IframeGatewayStatus, number> {
	const out: Record<IframeGatewayStatus, number> = {
		active: 0,
		paused: 0,
		archived: 0,
	};
	for (const row of list) {
		out[row.status] = (out[row.status] ?? 0) + 1;
	}
	return out;
}
